import moment from 'moment';

import LSK from 'constants/core/localStorageKey.const';
import { DEFAULT_LOCALE, LOCALE } from 'providers/GILT.provider';
import lsGet from 'utils/dom/storage/lsGet.util';
import nullBind from 'utils/functions/nullBind.util';
import isMoment from 'utils/predicates/isMoment.util';

const DEFAULT = 'Invalid date';

const LOCALIZED_TIME = 'LT';

const DAY_OF_WEEK = 'dddd';

const DAY_TO_YEAR_STANDARD = 'DD-MM-YYYY';
const DAY_TO_YEAR_ORDINAL = 'Do MMM YYYY';
const DAY_TO_SHORT_YEAR_ORDINAL = 'Do MMM YY';
const DAY_TO_YEAR_LONG = 'DD MMM YYYY';
const DAY_TO_YEAR_COMPACT = 'D MMM [‘]YY';
const DAY_TO_YEAR_SHORT = 'DD MMM YY';
const DAY_TO_YEAR_SHORTER = 'D MMM YYYY';
const DAY_TO_YEAR_DETAILED = 'ddd D MMM YYYY';
const DAY_TO_YEAR_WITH_TIME = 'D MMM YYYY, HH:mm';
const DAY_TO_MONTH = 'D MMM';
const DAY_TO_MONTH_LONG_LOCALE = 'dddd D MMM';
const DAY_TO_NAME_TO_MONTH = 'ddd D MMM';
const DAY_TO_YEAR_WITH_WEEKDAY_ORD = 'dddd Do MMM YY';

const DAY_TO_YEAR_WITH_WEEKDAY_INT = 'ddd DD MMM YYYY';
const DAY_TO_YEAR_WITH_SHORT_WEEKDAY = 'dd, D MMM YYYY';
const DAY_TO_YEAR_WITH_MID_WEEKDAY = 'ddd, D MMM Y';
const DAY_TO_YEAR_WITH_FULL_WEEKDAY = 'dddd, D MMM YYYY';
const DAY_TO_YEAR_WITH_FULL_WEEKDAY_SUFFIX = 'dddd Do MMM YYYY';
const DAY_TO_YEAR_WITH_AVERAGED_WEEKDAY = 'ddd D MMM [‘]YY';

const DAY_TO_HOUR = 'DDMMYYhh';

const MONTH_LONG = 'MMMM';
const MONTH_TO_YEAR_LONG = 'MMMM YYYY';

const HOUR_AND_MINUTE_MERIDIEM = 'hh:mm A';
const HOUR_AND_MINUTE_SHORT_MERIDIEM = 'h:mm A';
const HOUR_AND_MINUTE_SHORT = 'h:mm';
const HOUR_AND_MINUTE_HALF = 'hh:mm';
const HOUR_AND_MINUTE_FULL = 'HH:mm';
const HOUR_TO_YEAR = 'HH.mm DD MMM YYYY';

const PART_DATE = 'dddd, Do MMMM';
const PART_TIME = 'h:mma';
const PART_AM_PM = 'A';

const SEGMENT_WEEK = 'W[W]YYYY';

const YEAR_TO_DAY_LOCALIZED = 'll';
const YEAR_TO_DAY_STANDARD = 'YYYY-MM-DD';
const YEAR_TO_DAY_COMPACT = 'YYYYMMDD';
const YEAR_TO_SECOND_SN = 'YYMMDD-hhmmss';

const UNIX_MILLIS = 'x';

const formatWithMoment = (format, date) => {
  if (void 1 === date) return DEFAULT;
  if (isMoment(date)) return date.format(format);
  if (Number.isSafeInteger(date)) return moment(date).format(format);
  if (typeof date !== 'string') return DEFAULT;

  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(format) : DEFAULT;
};

const bind = (format) => {
  const fn = nullBind(formatWithMoment, format);
  fn.FORMAT = format;
  return fn;
};

export const formatDayOfWeek = bind(DAY_OF_WEEK);

export const formatDayToYeaCompact = bind(DAY_TO_YEAR_COMPACT);
export const formatDayToYear = bind(DAY_TO_YEAR_SHORT);
export const formatDayToYearShorter = bind(DAY_TO_YEAR_SHORTER);
export const formatDayToYearDetailed = bind(DAY_TO_YEAR_DETAILED);
export const formatDayToYearWithTime = bind(DAY_TO_YEAR_WITH_TIME);
export const formatDayToMonth = bind(DAY_TO_MONTH);
export const formatDayToMonthLongLocale = bind(DAY_TO_MONTH_LONG_LOCALE);
export const formatDayToNameToMonth = bind(DAY_TO_NAME_TO_MONTH);
export const formatDayToYearLong = bind(DAY_TO_YEAR_LONG);
export const formatDayToYearStandard = bind(DAY_TO_YEAR_STANDARD);
export const formatDayToYearOrdinal = bind(DAY_TO_YEAR_ORDINAL);
export const formatDayToShortYearOrdinal = bind(DAY_TO_SHORT_YEAR_ORDINAL);
export const formatDayToYearWeekday = bind(DAY_TO_YEAR_WITH_WEEKDAY_ORD);
export const formatDayToYearFullWeekdaySuffix = bind(DAY_TO_YEAR_WITH_FULL_WEEKDAY_SUFFIX);
export const formatDayToYearWeekdayInt = bind(DAY_TO_YEAR_WITH_WEEKDAY_INT);
export const formatDayToYearShortWeekday = bind(DAY_TO_YEAR_WITH_SHORT_WEEKDAY);
export const formatDayToYearMidWeekday = bind(DAY_TO_YEAR_WITH_MID_WEEKDAY);
export const formatDayToYearFullWeekday = bind(DAY_TO_YEAR_WITH_FULL_WEEKDAY);
export const formatDayToYearAveragedWeekday = bind(DAY_TO_YEAR_WITH_AVERAGED_WEEKDAY);
export const formatDayToHour = bind(DAY_TO_HOUR);

export const formatMonthLong = bind(MONTH_LONG);
export const formatMonthToYearLong = bind(MONTH_TO_YEAR_LONG);

export const formatHourAndMinute = bind(HOUR_AND_MINUTE_MERIDIEM);
export const formatHourAndMinuteShortMeridiem = bind(HOUR_AND_MINUTE_SHORT_MERIDIEM);
export const formatHourAndMinuteShort = bind(HOUR_AND_MINUTE_SHORT);
export const formatHourAndMinuteHalf = bind(HOUR_AND_MINUTE_HALF);
export const formatHourAndMinuteFull = bind(HOUR_AND_MINUTE_FULL);
export const formatHourToYear = bind(HOUR_TO_YEAR);

export const formatYearToDayLocalized = bind(YEAR_TO_DAY_LOCALIZED);
export const formatYearToDayStandard = bind(YEAR_TO_DAY_STANDARD);
export const formatYearToDayCompact = bind(YEAR_TO_DAY_COMPACT);
export const formatYearToSecondSn = bind(YEAR_TO_SECOND_SN);

export const formatPartDate = bind(PART_DATE);
export const formatPartTime = bind(PART_TIME);

/**
 * Formats a time to "h:mma" format (e.g., "2:30pm")
 * Uses the user's locale preference.
 * Does not depend on moment.
 *
 * @param {Date|number|string} date - Date object, timestamp, or date string
 * @returns {string} - Formatted time with AM/PM
 */
export const formatPartTimeNative = (date) => {
  if (date === void 0) return DEFAULT;
  if (typeof date === 'string' || typeof date === 'number') {
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return DEFAULT;
    date = parsed;
  }
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return DEFAULT;

  const locale = determineUserLocale();
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

export const formatPartAmPm = bind(PART_AM_PM);
export const formatSegmentWeek = bind(SEGMENT_WEEK);
export const formatSegmentDay = formatYearToDayCompact;
export const formatUnixMillis = bind(UNIX_MILLIS);

// NOTE: do not use localized time for anything but final display to user, don't take substrings for other purposes
export const formatLocalizedTime = bind(LOCALIZED_TIME);

// Util function that determines the current user locale.
// If no user locale exists, it falls back to default
export const determineUserLocale = () => {
  const selectedLocale = lsGet(LSK.locale, { quiet: true });

  return LOCALE[selectedLocale] || DEFAULT_LOCALE;
};

// Util function that returns formatted date depending on the selected locale.
// Supported formats: L, LL, LLLL and LLLL
export const formatDateByLocale = (date, options) => {
  const defaultLocale = determineUserLocale();

  const format = options?.dateFormat || 'L';
  const locale = options?.locale || defaultLocale;

  moment.locale(locale);

  return moment(date).clone().format(format);
};

// Util function that returns formatted time depending on the selected locale.
// Supported formats: https://momentjscom.readthedocs.io/en/latest/moment/07-customization/06-long-date-formats/
export const formatTimeByLocale = (date, options) => {
  const defaultLocale = determineUserLocale();

  const format = options?.dateTimeFormat || 'LT';
  const locale = options?.locale || defaultLocale;

  moment.locale(locale);

  const timeFormat = moment.localeData(locale).longDateFormat(format);

  return moment(date).clone().format(timeFormat);
};

/**
 * Formats a Date to short weekday (e.g., "Tue")
 * Uses the user's locale preference.
 *
 * @param {Date} date
 * @returns {string}
 */
export const formatShortWeekday = (date) => {
  const locale = determineUserLocale();
  return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
};

/**
 * Formats a date to "Month Year" format (e.g., "January 2026")
 * @param {Date} date - Date object
 * @returns {string} - Formatted month and year
 */
export const formatMonthYear = (date) =>
  new Intl.DateTimeFormat(determineUserLocale(), { month: 'long', year: 'numeric' }).format(date);

/**
 * Formats a date to "D MMM 'YY" format (e.g., "24 Jul '26")
 * Uses the user's locale preference.
 *
 * @param {Date|number|string} date - Date object, timestamp, or date string
 * @returns {string} - Formatted date
 */
export const formatMonthDayYear = (date) => {
  if (date === void 0) return DEFAULT;
  if (typeof date === 'string' || typeof date === 'number') {
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return DEFAULT;
    date = parsed;
  }
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return DEFAULT;

  const locale = determineUserLocale();
  const day = date.getDate();
  const month = date.toLocaleDateString(locale, { month: 'short' });
  const year = date.toLocaleDateString(locale, { year: '2-digit' });

  return `${day} ${month} '${year}`;
};
